import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { contentApi } from '@/lib/api';
import { Seo } from '@/components/Seo';
import { useLocale } from '@/hooks/useLocalePath';
import type { JSX } from 'react';

/**
 * Legal content, versioned, in both locales. Rendered from markdown-ish plain text, never
 * as raw HTML — the same rule that applies to supplier copy applies here.
 */
export function ContentPage(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const { slug = '' } = useParams();

  const { data, isLoading } = useQuery({
    queryKey: ['page', slug, locale],
    queryFn: () => contentApi.page(slug, locale),
  });

  if (isLoading) {
    return (
      <div className="rz-shell max-w-2xl py-16 space-y-4">
        <div className="rz-skeleton h-8 w-2/3" />
        {Array.from({ length: 8 }, (_, i) => <div key={i} className="rz-skeleton h-4 w-full" />)}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rz-shell py-32 text-center">
        <h1 className="font-display text-xl">{t('errors.notFound')}</h1>
      </div>
    );
  }

  return (
    <>
      <Seo title={data.title} path={`/strona/${slug}`} />
      <article className="rz-shell max-w-2xl py-16 space-y-5">
        <h1 className="text-xl md:text-2xl">{data.title}</h1>
        <p className="rz-eyebrow">
          {t('legal.version', { version: data.version, date: '2026-01-01' })}
          {data.isFallback && ' · pl'}
        </p>
        <div className="space-y-4">
          {data.body.split('\n\n').map((block, index) =>
            block.startsWith('## ') ? (
              <h2 key={index} className="pt-4 text-lg">{block.slice(3)}</h2>
            ) : block.startsWith('- ') ? (
              <ul key={index} className="space-y-1.5 pl-5 list-disc text-sm leading-relaxed text-[var(--color-ink-soft)]">
                {block.split('\n').map((line, i) => <li key={i}>{renderEmphasis(line.replace(/^- /, ''))}</li>)}
              </ul>
            ) : (
              <p key={index} className="text-sm leading-relaxed text-[var(--color-ink-soft)]">
                {renderEmphasis(block)}
              </p>
            ),
          )}
        </div>
      </article>
    </>
  );
}

/** `**bold**` only. No HTML is ever interpreted from content. */
function renderEmphasis(text: string): JSX.Element {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**') ? (
          <strong key={i} className="text-[var(--color-ink)]">{part.slice(2, -2)}</strong>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}
